import React from 'react';
import { Bot, MessageSquare, Zap, Shield, BarChart, ChevronLeft, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ChatbotService() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-700">
              <ChevronLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <a 
              href="https://wa.me/60177751631"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-green-600 hover:text-green-700"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Contact on WhatsApp</span>
            </a>
          </div>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <Bot className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">AI Chatbot Solutions</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Intelligent conversational AI that engages your customers 24/7, boosting satisfaction and sales.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold mb-6">Key Features</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <MessageSquare className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-semibold mb-1">Natural Language Processing</h3>
                  <p className="text-gray-600">Advanced AI that understands context and intent for human-like conversations.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Zap className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-semibold mb-1">Instant Responses</h3>
                  <p className="text-gray-600">24/7 availability with immediate, accurate responses to customer queries.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Shield className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-semibold mb-1">Secure Integration</h3>
                  <p className="text-gray-600">Enterprise-grade security with seamless integration into your existing systems.</p>
                </div>
              </div>
              <div className="flex items-start">
                <BarChart className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-semibold mb-1">Analytics Dashboard</h3>
                  <p className="text-gray-600">Comprehensive insights into customer interactions and chatbot performance.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&q=80"
              alt="AI Chatbot Interface"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Why Choose Our Chatbot?</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Customizable to match your brand voice</li>
                <li>• Multi-language support</li>
                <li>• Easy integration with popular platforms</li>
                <li>• Regular updates and improvements</li>
                <li>• Detailed analytics and reporting</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-8">Chatbot Solution Comparison</h2>
          <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Features</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-indigo-600">Our AI Chatbot</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Botsonic</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Snatchbot</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Starting Price</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">RM99/month</td>
                  <td className="px-6 py-4 text-center text-sm">RM199/month</td>
                  <td className="px-6 py-4 text-center text-sm">RM150/month</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Website Integration</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Yes (Included)</td>
                  <td className="px-6 py-4 text-center text-sm">Yes (Limited)</td>
                  <td className="px-6 py-4 text-center text-sm">Yes</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Branding Customization</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Full Control</td>
                  <td className="px-6 py-4 text-center text-sm">Limited</td>
                  <td className="px-6 py-4 text-center text-sm">Basic</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Multi-platform Support</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Yes (Web, WhatsApp, Telegram)</td>
                  <td className="px-6 py-4 text-center text-sm">Web Only</td>
                  <td className="px-6 py-4 text-center text-sm">Yes (Additional Cost)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Analytics Dashboard</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Comprehensive</td>
                  <td className="px-6 py-4 text-center text-sm">Basic</td>
                  <td className="px-6 py-4 text-center text-sm">Limited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Setup & Training</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Included</td>
                  <td className="px-6 py-4 text-center text-sm">Self-service Only</td>
                  <td className="px-6 py-4 text-center text-sm">Basic Support</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">24/7 Support</td>
                  <td className="px-6 py-4 text-center text-sm text-indigo-600">Yes</td>
                  <td className="px-6 py-4 text-center text-sm">Email Only</td>
                  <td className="px-6 py-4 text-center text-sm">Email & Chat</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-center">
          <Link 
            to="/#contact"
            className="inline-flex items-center bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 transition"
          >
            Get Started with AI Chatbot
          </Link>
        </div>
      </div>
    </div>
  );
}